import { Box, Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import AddDomainModal from "../components/Domain/AddDomainModal";
import EditDomainModal from "../components/Domain/EditDomainModal";
import Loader from "../components/general/Loader";
import LoadingBackdrop from "../components/general/LoadingBackdrop";
import NoData from "../components/general/NoData";
import ReusableButton from "../components/general/ReusableButton";
import ApproveModal from "../components/modal/ApproveModal";
import DeleteModal from "../components/modal/DeleteModal";
import HeavyTable from "../components/table/HeavyTable";
import { showWarningSwal } from "../functions/alert";
import { deleteDomain } from "../functions/deleteData";
import {
  buildQueryString,
  cancelAction,
  changeViewOptions,
  
  
  makeSureIsArray,
} from "../functions/general";
import { getDomainsAndTags } from "../functions/getData";
import { approveDomain } from "../functions/patchData";
import { showNotification } from "../functions/snackbar";

import PageHeader from "../components/ui/PageHeader";
import TableCard from "../components/ui/TableCard";
import TableToolbar from "../components/ui/TableToolbar";
import SearchInput from "../components/ui/SearchInput";
import { useEnhancedFilters } from "../components/ui/useFilterDrawer";
const domainHeader = [
  { id: "topic_domain", label: "Domain" },
  { id: "name", label: "Topic" },
  { id: "tag", label: "Tag" },
  { id: "created_by", label: "Created By" },
  { id: "last_modified_by", label: "Last Modified By" },
  { id: "status", label: "Status" },
];

export default function EnhancedDomain() {
  document.title = "Domains and Tags";
  const isMounted = useRef(false);
  const [header, setHeader] = useState(domainHeader);
  const [domainTable, setDomainTable] = useState();
  const [viewOptions, setViewOptions] = useState([
    { label: "Created By", state: true, key: "created_by_username" },
    { label: "Last Modified By", state: true, key: "modified_by_username" },
    { label: "Status", state: true, key: "status" },
  ]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [id, setId] = useState("");
  const [selected, setSelected] = useState([]);
  const [selecting, setSelecting] = useState([]);
  const [onDelete, setOnDelete] = useState(false);
  const [onAddDomain, setOnAddDomain] = useState(false);
  const [onEditDomain, setOnEditDomain] = useState(false);
  const [onApprove, setOnApprove] = useState(false);
  const [domainOptions, setDomainOptions] = useState([]);
  const [sortAbleColumns, setSortableColumns] = useState([]);

  // Consolidated query parameters state
  const [queryParams, setQueryParams] = useState({
    limits: 10,
    page: 1,
    search: "",
    domains: "",
    status: "",
    orderBy: "",
    orderDirection: "",
  });

  const cancelDelete = () => cancelAction(setOnDelete, setId);
  const cancelAddDomain = () => cancelAction(setOnAddDomain);
  const cancelEdit = () => cancelAction(setOnEditDomain, setId);
  const cancelApprove = () => cancelAction(setOnApprove, setId);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const afterFunction = (cancel, data) => {
    if ([200, 201].includes(data.status)) {
      if (controller.current) {
        setSelected([]);
        setSelecting([]);
        setLoading(true);

        const queryString = buildQueryString(queryParams);
        getDomainsAndTags(queryString, controller.current.signal).then(
          (res) => {
            setDomainTable(res);
            setSortableColumns(res?.sortable_columns);
            setLoading(false);
          }
        );
      }
      cancel();
      showNotification("success", data.message, enqueueSnackbar, closeSnackbar);
    }
  };

  const updateQueryParams = (updates) => {
    setQueryParams((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const resetQueryParams = () => {
    setQueryParams({
      limits: 10,
      page: 1,
      search: "",
      domains: "",
      status: "",
      orderBy: "",
      orderDirection: "",
    });
  };

  const adjustEndpoint = (endpoint, value, params = queryParams) => {
    const currentParams = { ...params };

    // Remove the current filter value if it's being changed
    if (value !== currentParams.domains && currentParams.domains) {
      endpoint += `&domains=${currentParams.domains}`;
    }
    if (value !== currentParams.status && currentParams.status) {
      endpoint += `&status=${currentParams.status}`;
    }

    return endpoint;
  };

  const filters = [
    {
      label: "Domain",
      endpoint: "tag/list?get_filter=domains",
      value: queryParams.domains,
      onChange: (value) => updateQueryParams({ domains: value }),
      key: "domains",
      setValue: (value) => updateQueryParams({ domains: value }),
    },
    {
      label: "Status",
      endpoint: "tag/list?get_filter=status",
      value: queryParams.status,
      onChange: (value) => updateQueryParams({ status: value }),
      key: "status",
      setValue: (value) => updateQueryParams({ status: value }),
    },
  ];

  const deleteFunction = () => {
    setLoading(true);
    deleteDomain({ id: selected.length > 0 ? selected : [id] })
      .then((d) => {
        afterFunction(cancelDelete, d);
      })
      .catch((e) => console.log(e))
      .finally(() => isMounted.current && setLoading(false));
  };

  const approveFunction = () => {
    setLoading(true);
    approveDomain({ id: selected.length > 0 ? selected : [id] })
      .then((d) => {
        afterFunction(cancelApprove, d);
      })
      .catch((e) => console.log(e))
      .finally(() => isMounted.current && setLoading(false));
  };

  const handleSortingFunction = (sortingKey, direction) => {
    updateQueryParams({
      orderBy: sortingKey,
      orderDirection: direction,
    });
  };

  const onChangeViewOptions = (value) =>
    changeViewOptions(domainHeader, setViewOptions, setHeader, value);

  const onChangeSearch = (value) => {
    setSearch(value);
    updateQueryParams({ search: value });
  };

  const onChangePage = (pageNo) => {
    updateQueryParams({ page: pageNo });
  };

  const onChangeRowsPerPage = (numberOfRows) => {
    setRowsPerPage(numberOfRows);
    updateQueryParams({ limits: numberOfRows });
  };

  const controller = useRef(null);
  const isFirstRender = useRef(true);

  // Effect for initial data fetch
  useEffect(() => {
    if (isFirstRender.current) isFirstRender.current = false;
    isMounted.current = true;

    // Create a new abort controller for this effect
    controller.current = new AbortController();

    // Initial data fetch
    setSelected([]);
    setSelecting([]);
    setLoading(true);

    const queryString = buildQueryString(queryParams);
    getDomainsAndTags(queryString, controller.current.signal).then((res) => {
      setDomainTable(res);
      setSortableColumns(res?.sortable_columns);
      setLoading(false);
    });

    // Load domain options for modals
    getDomainsAndTags("?get_filter=domains", controller.current.signal)
      .then((d) => {
        const mappedOptions = d.map((domain) => {
          return {
            name: domain.filter_name,
            id: domain.filter_id,
          };
        });
        setDomainOptions(mappedOptions);
      })
      .catch((e) => console.log(e));

    return () => {
      if (controller.current) {
        controller.current.abort();
      }
      isMounted.current = false;
    };
  }, []);

  // Effect for data fetching when query params change
  useEffect(() => {
    if (controller.current && !isFirstRender.current) {
      setSelected([]);
      setSelecting([]);
      setLoading(true);

      const queryString = buildQueryString(queryParams);
      getDomainsAndTags(queryString, controller.current.signal).then((res) => {
        setDomainTable(res);
        setSortableColumns(res?.sortable_columns);
        setLoading(false);
      });
    }
  }, [queryParams]);

  const onClickMassApprove = () => {
    return domainTable && selected.length > 0
      ? setOnApprove(true)
      : showWarningSwal(
          "Please select pending domains / tags only to mass approve"
        );
  };

  const onClickMassDelete = () => {
    return selected.length
      ? setOnDelete(true)
      : showWarningSwal("Please select at least one domain to mass delete");
  };

  const enhancedFilters = useEnhancedFilters({ filters, adjustEndpoint });

  const total = domainTable?.total ?? domainTable?.meta?.total;

  return (
    <Box className="wrapperBox simcc-page">
      <LoadingBackdrop loading={loading} />
      <PageHeader
        title="Domains and Tags"
        actions={
          <>
            <>
              <ReusableButton variant="primary" text="Add Domain / Tag" onClick={() => setOnAddDomain(true)} iconType="add" />
              <ReusableButton variant="danger" text="Mass Delete" onClick={() => onClickMassDelete()} iconType="delete" />
              <ReusableButton variant="ghost" text="Mass Approve" onClick={() => onClickMassApprove()} iconType="check" />
            </>
          </>
        }
      />
      <TableCard
        toolbar={
          <TableToolbar
            search={<SearchInput state={search} setState={onChangeSearch} placeholder="Search domain, topic…" ariaLabel={"Search only in \"Domain\", \"Topic\", and \"Tag\" column"} />}
            onOpenFilters={enhancedFilters.openDrawer}
            filtersOpen={enhancedFilters.isOpen}
            filterCount={enhancedFilters.count}
            meta={total !== undefined && `${Number(total).toLocaleString()} domains and tags`}
          />
        }
        chips={enhancedFilters.chips}
      >
        {loadingTable && <Loader height={600} />}
        {!loadingTable &&
          domainTable &&
          makeSureIsArray(domainTable.data).length && (
            <HeavyTable bare onChangeRowsPerPage={onChangeRowsPerPage}
              handleSortingFunction={handleSortingFunction}
              sortableKeys={sortAbleColumns}
              headers={header}
              list={domainTable}
              title="domain"
              rowsPerPage={rowsPerPage}
              setOnDelete={setOnDelete}
              setOnApprove={setOnApprove}
              setOnEdit={setOnEditDomain}
              onChangePage={onChangePage}
              fixed={["domain_name", "name", "tag"]}
              selecting={selecting}
              setSelecting={setSelecting}
              selected={selected}
              setSelected={setSelected}
              setId={setId}
            />
          )}
        {!loadingTable &&
          domainTable &&
          !makeSureIsArray(domainTable.data).length && <NoData height={600} />}
      </TableCard>
      {enhancedFilters.drawer}
      <Modal open={onDelete} onClose={() => cancelDelete()}>
        <>
          {Boolean(domainTable) && (
            <DeleteModal
              table={domainTable}
              id={id}
              selected={selected}
              deleteFunction={deleteFunction}
              cancelDelete={cancelDelete}
            />
          )}
        </>
      </Modal>
      <Modal open={onApprove} onClose={() => cancelApprove()}>
        <>
          {Boolean(domainTable) && (
            <ApproveModal
              table={domainTable}
              id={id}
              selected={selected}
              approveFunction={approveFunction}
              cancelApprove={cancelApprove}
              object="domain/tags"
            />
          )}
        </>
      </Modal>
      <Modal open={onAddDomain} onClose={() => cancelAddDomain()}>
        <>
          <AddDomainModal
            setOnAddDomain={setOnAddDomain}
            afterAddingDomain={() => {
              const queryString = buildQueryString(queryParams);
              getDomainsAndTags(queryString, controller.current.signal).then(
                (res) => {
                  setDomainTable(res);
                  setSortableColumns(res?.sortable_columns);
                }
              );
            }}
            domainOptions={domainOptions}
          />
        </>
      </Modal>
      <Modal open={onEditDomain} onClose={() => cancelEdit()}>
        <>
          {domainTable && (
            <EditDomainModal
              table={domainTable}
              id={id}
              afterEditingDomain={() => {
                const queryString = buildQueryString(queryParams);
                getDomainsAndTags(queryString, controller.current.signal).then(
                  (res) => {
                    setDomainTable(res);
                    setSortableColumns(res?.sortable_columns);
                  }
                );
              }}
              cancelEdit={cancelEdit}
            />
          )}
        </>
      </Modal>
    </Box>
  );
}
